import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { FormField } from '@/components/shared/form-field';
import { LoadingButton } from '@/components/shared/loading-button';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import type { Installation } from '@/lib/types';

/** Mirrors testDeviceNameSchema in packages/shared/src/schemas/installations.ts. */
const MAX_NAME = 64;

/** Add a device as a named test device, or rename one that already is. */
export function TestDeviceDialog({ appId, device, onClose }: { appId: string; device: Installation | null; onClose(): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const renaming = !!device?.is_test_device;
  // Start from the current name each time the dialog opens for a device.
  useEffect(() => { if (device) setName(device.test_device_name ?? ''); }, [device]);

  const save = useMutation({
    mutationFn: (d: Installation) => api<Installation>(`/admin/v1/apps/${appId}/installations/${d.installation_id}`, {
      method: 'PATCH', body: { is_test_device: true, test_device_name: name.trim() },
    }),
    onSuccess: (d) => {
      toast(renaming ? `Renamed to ${d.test_device_name}` : `${d.test_device_name} added as a test device`);
      void qc.invalidateQueries({ queryKey: ['installations', appId] });
      close();
    },
  });
  const close = () => { save.reset(); onClose(); };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (device && name.trim()) save.mutate(device);
  };

  return (
    <Dialog open={!!device} onOpenChange={(open) => { if (!open && !save.isPending) close(); }}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{renaming ? 'Rename test device' : 'Add as test device'}</DialogTitle>
            <DialogDescription>
              {renaming
                ? 'Change the name shown for this test device.'
                : 'Test devices receive notifications sent to "All test devices" from Send Notification. Give it a name you will recognise.'}
            </DialogDescription>
          </DialogHeader>
          {device && (
            <p className="rounded-md border px-3 py-2 font-mono text-xs break-all text-muted-foreground">
              {device.platform} · {device.device_model ?? 'unknown model'} · {device.installation_id}
            </p>
          )}
          <FormField label="Name" htmlFor="test-device-name" hint="e.g. QA Pixel 8, Product iPhone">
            <Input id="test-device-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={MAX_NAME} autoFocus autoComplete="off" placeholder="e.g. QA Pixel 8" />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={save.isPending} onClick={close}>Cancel</Button>
            <LoadingButton type="submit" disabled={!name.trim()} loading={save.isPending}>
              <FlaskConical /> {renaming ? 'Save name' : 'Add test device'}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
